import { z } from 'zod';
// -------- Invoices (0039, extended by M2a / 0076) --------
// The dispatcher-side write surface. Money is always integer minor units on
// the wire as well as in the database: a JSON number with a decimal point is
// how pence become floats.
export const invoiceStatusValues = [
    'draft',
    'sent',
    'paid',
    'void',
    'overdue',
];
export const invoiceStatusSchema = z.enum(invoiceStatusValues);
export const vatCodeValues = ['standard', 'reduced', 'zero', 'exempt'];
export const vatCodeSchema = z.enum(vatCodeValues);
export const paymentMethodValues = [
    'bank_transfer',
    'card',
    'cash',
    'cheque',
    'direct_debit',
    'other',
];
export const paymentMethodSchema = z.enum(paymentMethodValues);
const idSchema = z.string().length(26);
/** Integer pence, never a float, and never negative on the wire. */
const penceSchema = z.number().int().min(0);
export const invoiceLineInputSchema = z.object({
    description: z.string().min(1).max(200),
    quantity: z.number().int().min(1).max(10_000),
    unitPricePence: penceSchema,
    /** Fraction, not percent: 0.2 is 20%. */
    vatRate: z.number().min(0).max(1),
    vatCode: vatCodeSchema.optional(),
    loadId: idSchema.nullish(),
    loadReference: z.string().max(40).nullish(),
});
export const createInvoiceBodySchema = z.object({
    customerId: idSchema,
    notes: z.string().max(2000).nullish(),
    dueAt: z.string().datetime().nullish(),
    lines: z.array(invoiceLineInputSchema).min(1).max(200),
});
/** Build a draft from completed, unbilled loads — the common path. */
export const createInvoiceFromLoadsBodySchema = z.object({
    customerId: idSchema,
    loadIds: z.array(idSchema).min(1).max(200),
    notes: z.string().max(2000).nullish(),
    dueAt: z.string().datetime().nullish(),
});
export const replaceInvoiceLinesBodySchema = z.object({
    lines: z.array(invoiceLineInputSchema).min(1).max(200),
});
export const issueInvoiceBodySchema = z.object({
    /** Invoices are commonly dated to a period end. Settable at issue only. */
    issuedAt: z.string().datetime().optional(),
});
export const recordPaymentBodySchema = z.object({
    receivedAt: z.string().datetime(),
    /** Strictly positive: a zero or negative "payment" is a credit note. */
    amountPence: z.number().int().min(1),
    method: paymentMethodSchema.optional(),
    reference: z.string().max(80).nullish(),
    notes: z.string().max(2000).nullish(),
});
export const createCreditNoteBodySchema = z.object({
    reason: z.string().max(200).nullish(),
    notes: z.string().max(2000).nullish(),
    /** Omit to credit the whole invoice, line for line. */
    lines: z.array(invoiceLineInputSchema).max(200).optional(),
});
export const listInvoicesQuerySchema = z.object({
    status: invoiceStatusSchema.optional(),
    customerId: idSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
export const billableLoadsQuerySchema = z.object({
    customerId: idSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
/**
 * Numbering configuration. Accepted only before the fleet's first issue — the
 * database refuses it afterwards, so this schema validates shape, not
 * permission.
 */
export const configureSequenceBodySchema = z.object({
    prefix: z
        .string()
        .regex(/^[A-Z0-9][A-Z0-9/-]{0,7}$/, 'Use up to 8 characters: A-Z, 0-9, - or /')
        .optional(),
    pad: z.number().int().min(1).max(12).optional(),
    startNumber: z.number().int().min(1).max(9_999_999_999).optional(),
});
export const invoiceIdParamSchema = z.object({
    fleetId: idSchema,
    invoiceId: idSchema,
});
export const paymentIdParamSchema = z.object({
    fleetId: idSchema,
    invoiceId: idSchema,
    paymentId: idSchema,
});
export const creditNoteIdParamSchema = z.object({
    fleetId: idSchema,
    creditNoteId: idSchema,
});
//# sourceMappingURL=invoice.js.map